// Agent tuning suggestions — AgentTroubleshooting (Technical) companion
// view. Analyzes the agent's current config (actions + description) +
// problematic sessions in a window and surfaces evidence-grounded
// suggestions. Paste the agent's current instruction text to unlock
// instruction-level recommendations; without it the suggestions stay at
// the action/prompt level (Cisco gates the instruction field).
//
// Server-side fan-out lives in the summary Lambda (`report: 'agent-tuning'`).

import React, { useCallback, useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Code,
  Divider,
  Group,
  Loader,
  NumberInput,
  Paper,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconChecks,
  IconHammer,
  IconSparkles,
} from '@tabler/icons-react';

import { useAuth } from '../../../../shared/contexts/AuthContext';
import { fetchAgentTuning, isWxccAiOpsBackendConfigured } from '../../api_scripts/wxccAiOpsClient';
import { Breadcrumbs, ErrorAlert, fmtTs, short, toDatetimeLocal } from './utils';

const SEVERITY_COLOR = { high: 'red', medium: 'orange', low: 'yellow' };
const CONFIDENCE_COLOR = { high: 'teal', medium: 'blue', low: 'gray' };
const SCOPE_LABEL = {
  goals: 'Goals / Instructions',
  action: 'Existing action',
  add_action: 'New action',
  prompt: 'Prompt text',
};

export function AgentTuning({ agentId, agentName, setView }) {
  const { getApiToken } = useAuth();
  const configured = isWxccAiOpsBackendConfigured();
  const nowIso = toDatetimeLocal(new Date());
  const weekAgoIso = toDatetimeLocal(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));

  // API Gateway REST API has a hard 29-second integration timeout. Each
  // audit-ai-agent call is serialized at 2.2s (Cisco OPS rate limit), so
  // audit time = sampleSize * 2.2s. On top of that Bedrock Sonnet 4.6
  // typically takes 10-20s for the tuning prompt. Total budget: ~29s.
  //
  // Safe sampleSize ≤ 4 (4 * 2.2 = 8.8s audit + ~15s Bedrock = ~24s).
  // 5 is borderline; 6+ will usually 504 the gateway.
  const [form, setForm] = useState({
    from: weekAgoIso,
    to: nowIso,
    sampleSize: 3,
    pastedInstructions: '',
  });
  const [state, setState] = useState({ loading: false, report: null, error: null });

  const runAnalysis = useCallback(async () => {
    const fromMs = new Date(form.from).getTime();
    const toMs = new Date(form.to).getTime();
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || fromMs >= toMs) {
      setState({ loading: false, report: null, error: 'Pick a valid from/to window.' });
      return;
    }
    setState({ loading: true, report: null, error: null });
    try {
      const report = await fetchAgentTuning(
        {
          agentId,
          from: fromMs,
          to: toMs,
          pastedInstructions: form.pastedInstructions.trim() || null,
          sampleSize: form.sampleSize,
        },
        getApiToken,
      );
      setState({ loading: false, report, error: null });
    } catch (e) {
      setState({ loading: false, report: null, error: e.message });
    }
  }, [agentId, form, getApiToken]);

  return (
    <Stack>
      <Breadcrumbs
        items={[
          { label: 'AI Agents', onClick: () => setView({ name: 'ai-agents' }) },
          { label: agentName || agentId, onClick: () => setView({ name: 'sessions', agentId, agentName }) },
          { label: 'Tuning' },
        ]}
      />

      <Group gap="xs">
        <IconSparkles size={20} color="#1976d2" />
        <Title order={3}>Tuning suggestions · {agentName || agentId}</Title>
      </Group>
      <Text size="sm" c="dimmed">
        Analyses this bot's config + a sample of problematic sessions in the window and surfaces
        evidence-grounded tuning suggestions. Pasting the current instruction / goals text unlocks
        instruction-level recommendations — Cisco gates that field on the audit tool so we need
        it from you to compare. The Lambda prompt is pre-seeded with WxCC platform constraints,
        channel-specific behavior rules, botType-aware tuning tactics (scripted <code>mlbot</code>
        vs autonomous <code>virtualagent</code>), and our documented anti-patterns (entry/exit
        context chaining, configurable slot skip, deep JSON path in Case, per-question answer
        intents, resetState=false, etc.) — so the LLM won't suggest patterns we already know
        don't work and will flag the agent if it's using one.
      </Text>

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Group grow>
            <TextInput
              label="From"
              type="datetime-local"
              value={form.from}
              onChange={(e) => setForm((f) => ({ ...f, from: e.currentTarget.value }))}
            />
            <TextInput
              label="To"
              type="datetime-local"
              value={form.to}
              onChange={(e) => setForm((f) => ({ ...f, to: e.currentTarget.value }))}
            />
            <NumberInput
              label="Sample size"
              value={form.sampleSize}
              onChange={(v) => setForm((f) => ({ ...f, sampleSize: typeof v === 'number' ? v : 3 }))}
              min={2}
              max={5}
              step={1}
              description={`~${Math.round(form.sampleSize * 2.2 + 15)}s total (audit + Bedrock). 29s gateway limit.`}
            />
          </Group>
          <Textarea
            label="Paste current instruction / goals text (optional)"
            description="Cisco's audit tool returns the agent's description + action catalog but NOT the instruction text. Paste it here if you want instruction-level suggestions in the output."
            autosize
            minRows={4}
            maxRows={14}
            value={form.pastedInstructions}
            onChange={(e) => setForm((f) => ({ ...f, pastedInstructions: e.currentTarget.value }))}
            placeholder="Example: You are a friendly, professional appointment assistant for Prisma Health Orthopedics..."
          />
          <Group>
            <Button
              leftSection={<IconHammer size={14} />}
              loading={state.loading}
              disabled={!configured}
              onClick={runAnalysis}
            >
              {state.report ? 'Re-run analysis' : 'Run analysis'}
            </Button>
            {!configured && (
              <Text size="xs" c="dimmed">
                Backend not configured. Set <Code>REACT_APP_CCUTILS_API_BASE_URL</Code>.
              </Text>
            )}
          </Group>
        </Stack>
      </Paper>

      {state.loading && (
        <Stack gap="xs" align="center" py="xl">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Fetching config + up to {form.sampleSize} problematic sessions. Rate-limited at ~1 req / 2s —
            allow ~{Math.round(form.sampleSize * 2.2)}s before Bedrock runs.
          </Text>
        </Stack>
      )}

      {state.error && <TuningErrorAlert message={state.error} sampleSize={form.sampleSize} />}

      {!state.loading && state.report && <ReportBody report={state.report} setView={setView} />}
    </Stack>
  );
}

// 504 / "Endpoint request timed out" is the characteristic signature of
// API Gateway's hard 29-second integration timeout — the Lambda is still
// running but the gateway disconnected. Converting the opaque message
// into a plain-English prompt to rerun with a smaller sample.
function TuningErrorAlert({ message, sampleSize }) {
  const isTimeout = /504|timed out|Endpoint request timed out/i.test(message);
  if (isTimeout) {
    return (
      <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Timed out at the API gateway">
        <Text size="sm" mb="xs">
          API Gateway has a hard 29-second limit per request. Audit for {sampleSize} sessions plus
          Bedrock probably crossed that line.
        </Text>
        <Text size="sm">
          Try dropping Sample size to {Math.max(2, sampleSize - 1)} and running again. (Longer-term
          fix would be an async job pattern — client polls for the result — but the quick path is just
          a smaller sample.)
        </Text>
      </Alert>
    );
  }
  return <ErrorAlert message={message} />;
}

function ReportBody({ report, setView }) {
  const {
    agent,
    actionsCatalog,
    promptsAvailability,
    instructionsSource,
    sessionsInWindow,
    problematicCount,
    sampledSessions,
    suggestions,
    narrative,
    insufficientEvidenceNote,
    generatedAt,
  } = report;

  return (
    <Stack gap="md">
      <Paper withBorder p="md" radius="md" style={{ background: '#f8fbff' }}>
        <Group gap="xs" mb="xs">
          <IconChecks size={16} color="#1976d2" />
          <Title order={5}>Scope of this analysis</Title>
        </Group>
        <Group gap="md" wrap="wrap">
          <Stat label="Sessions in window" value={sessionsInWindow} />
          <Stat label="Problematic" value={problematicCount} />
          <Stat label="Sampled" value={sampledSessions} />
          <Stat label="Actions on bot" value={actionsCatalog?.length ?? 0} />
          <Stat label="Instructions source" value={<Badge size="xs" color={instructionsSource === 'pasted' ? 'teal' : 'orange'}>{instructionsSource}</Badge>} raw />
          <Stat label="Generated" value={fmtTs(generatedAt, true)} />
        </Group>
        {insufficientEvidenceNote && (
          <Alert color="orange" mt="sm" variant="light" icon={<IconAlertTriangle size={14} />}>
            {insufficientEvidenceNote}
          </Alert>
        )}
        {instructionsSource === 'none' && (
          <Alert color="yellow" mt="sm" variant="light" icon={<IconAlertTriangle size={14} />}>
            No instructions pasted — suggestions are limited to action-level and prompt-level changes.
            Paste the current instruction text and re-run to unlock instruction-level suggestions.
          </Alert>
        )}
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Title order={5} mb="xs">Agent snapshot</Title>
        <Text size="xs" c="dimmed" mb={4}>Name</Text>
        <Text size="sm" fw={500} mb="xs">{agent?.name} <Code>({agent?.botType})</Code></Text>
        <Text size="xs" c="dimmed" mb={4}>Description</Text>
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }} mb="sm">{agent?.description || <em>none</em>}</Text>
        <Text size="xs" c="dimmed" mb={4}>Prompts</Text>
        <Group gap={4}>
          {Object.entries(promptsAvailability ?? {}).map(([k, v]) => (
            <Badge key={k} size="xs" color={v === 'AVAILABLE' ? 'teal' : 'gray'} variant="light">
              {k}: {v}
            </Badge>
          ))}
        </Group>
        <Divider my="sm" />
        <Text size="xs" c="dimmed" mb={4}>Actions catalog</Text>
        <Stack gap={4}>
          {(actionsCatalog ?? []).map((a) => (
            <Group key={a.name} gap="xs" wrap="nowrap">
              <Badge size="xs" color={a.enabled ? 'teal' : 'gray'} variant="light">{a.enabled ? 'ON' : 'OFF'}</Badge>
              {a.systemTool && <Badge size="xs" color="blue" variant="outline">system</Badge>}
              <Text size="sm" fw={500}>{a.name}</Text>
              <Text size="xs" c="dimmed" style={{ flex: 1 }}>— {a.description?.slice(0, 160)}{a.description && a.description.length > 160 ? '…' : ''}</Text>
            </Group>
          ))}
        </Stack>
      </Paper>

      {narrative && (
        <Paper withBorder p="md" radius="md" style={{ background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)' }}>
          <Group gap="xs" mb="xs">
            <IconSparkles size={16} color="#1976d2" />
            <Title order={5}>Opening analysis</Title>
          </Group>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{narrative}</Text>
        </Paper>
      )}

      <Title order={5}>Suggestions ({suggestions?.length ?? 0})</Title>
      {(!suggestions || suggestions.length === 0) ? (
        <Alert color="gray" variant="light">
          No concrete tuning suggestions produced. Could mean: nothing is wrong, insufficient evidence in the window,
          or audit data was sparse. Try a wider window or larger sample.
        </Alert>
      ) : (
        <Stack gap="sm">
          {suggestions.map((s, i) => <SuggestionCard key={i} s={s} setView={setView} />)}
        </Stack>
      )}
    </Stack>
  );
}

function SuggestionCard({ s, setView }) {
  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" align="flex-start" mb="xs">
        <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
          <Badge color={SEVERITY_COLOR[s.severity] ?? 'gray'} variant="filled">{s.severity ?? '—'}</Badge>
          <Badge color="violet" variant="light">{SCOPE_LABEL[s.scope] ?? s.scope ?? '—'}</Badge>
          {s.actionName && <Badge color="gray" variant="outline">{s.actionName}</Badge>}
          <Code style={{ fontSize: 11 }}>{s.pattern}</Code>
          <Tooltip label={`Confidence: ${s.confidence ?? 'unknown'} · ${s.evidenceCount ?? 0} supporting sessions`}>
            <Badge color={CONFIDENCE_COLOR[s.confidence] ?? 'gray'} variant="light">conf: {s.confidence ?? '?'}</Badge>
          </Tooltip>
        </Group>
      </Group>

      <Text size="xs" c="dimmed" mb={2}>Rationale</Text>
      <Text size="sm" mb="sm" style={{ lineHeight: 1.55 }}>{s.rationale}</Text>

      <Text size="xs" c="dimmed" mb={2}>Proposed change</Text>
      <Paper bg="gray.0" p="xs" radius="sm" mb="sm">
        <Text size="sm" style={{ whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
          {s.proposedChange}
        </Text>
      </Paper>

      {s.risks && (
        <>
          <Text size="xs" c="dimmed" mb={2}>Risks</Text>
          <Text size="sm" mb="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>{s.risks}</Text>
        </>
      )}

      {Array.isArray(s.supportingSessions) && s.supportingSessions.length > 0 && (
        <>
          <Text size="xs" c="dimmed" mb={4}>Supporting sessions ({s.supportingSessions.length})</Text>
          <Stack gap={4}>
            {s.supportingSessions.map((ss, i) => (
              <Group key={i} gap="xs" wrap="nowrap">
                <Anchor
                  size="xs"
                  style={{ cursor: 'pointer', fontFamily: 'monospace' }}
                  onClick={() => {
                    const now = Date.now();
                    const twelveHours = 12 * 60 * 60 * 1000;
                    setView({
                      name: 'call-detail',
                      interactionId: ss.interactionId,
                      window: { from: now - twelveHours, to: now + twelveHours },
                      cachedAiSessions: null,
                    });
                  }}
                >
                  {short(ss.interactionId)}
                </Anchor>
                <Badge size="xs" variant="light">{ss.outcome}</Badge>
                {ss.evalScore != null && <Badge size="xs" variant="light" color="gray">eval {Number(ss.evalScore).toFixed(2)}</Badge>}
                <Text size="xs" c="dimmed" style={{ flex: 1, minWidth: 0 }}>{ss.notes}</Text>
              </Group>
            ))}
          </Stack>
        </>
      )}
    </Paper>
  );
}

function Stat({ label, value, raw }) {
  return (
    <Box>
      <Text size="xs" c="dimmed">{label}</Text>
      {raw ? value : <Text size="sm" fw={600}>{value}</Text>}
    </Box>
  );
}
